import React, { useState } from 'react';
import { User, Menu, LogOut, Key, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { ChangePasswordDialog } from '@/components/settings/ChangePasswordDialog';
import { buildPathWithPreviewToken } from '@/lib/previewEnvironment';

interface EmpHeaderProps {
  title: string;
  subtitle?: string;
  headerActions?: React.ReactNode;
  onMobileMenuToggle?: () => void;
}

const EMP_ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador',
  coadmin: 'Gestão Contratual',
  gestor: 'Fiscal de Medição',
  executor: 'Executor de Campo',
};

const EmpHeader: React.FC<EmpHeaderProps> = ({ title, subtitle, headerActions, onMobileMenuToggle }) => {
  const { user, role, signOut } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  const navigateWithPreviewToken = (path: string) => navigate(buildPathWithPreviewToken(path));

  const displayName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'Usuário';
  const roleLabel = role ? EMP_ROLE_LABELS[role] || role : '';

  return (
    <header className="sticky top-0 z-40 border-b" style={{
      backgroundColor: 'hsla(0, 0%, 100%, 0.92)',
      backdropFilter: 'blur(12px)',
      borderColor: 'hsl(0, 0%, 90%)',
    }}>
      <div className="flex items-center justify-between h-[60px] sm:h-16 px-4 sm:px-6">
        {/* Left */}
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden min-h-[44px] min-w-[44px] active:scale-95 transition-transform flex-shrink-0 text-foreground/60 hover:text-foreground"
            onClick={onMobileMenuToggle}
          >
            <Menu className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5">
              <h1 className="text-base sm:text-lg font-semibold text-foreground truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {title}
              </h1>
            </div>
            {subtitle && <p className="text-xs text-muted-foreground truncate mt-0.5">{subtitle}</p>}
          </div>
          {headerActions && (
            <div className="hidden md:flex items-center ml-4 flex-shrink-0">{headerActions}</div>
          )}
        </div>

        {/* Right */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {user && (
            <>
              <NavLink
                to="/empreendimentos/manual"
                className={({ isActive }) =>
                  cn(
                    'hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 min-h-[40px]',
                    isActive
                      ? 'text-emp-primary font-medium bg-emp-primary/10'
                      : 'text-foreground/60 hover:text-foreground hover:bg-foreground/5'
                  )
                }
              >
                <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden lg:inline">Manual</span>
              </NavLink>
              <div className="flex items-center gap-2 sm:gap-3 pl-3 sm:pl-4 border-l" style={{ borderColor: 'hsl(0, 0%, 90%)' }}>
                <div className="hidden md:flex flex-col items-end">
                  <p className="text-sm font-medium text-foreground">{displayName}</p>
                  {roleLabel && <span className="text-xs text-muted-foreground">{roleLabel}</span>}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full min-h-[40px] min-w-[40px] active:scale-95 transition-all duration-200 bg-emp-primary/10 text-emp-primary hover:bg-emp-primary/20"
                    >
                      <User className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <p className="text-sm font-medium">{displayName}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      {roleLabel && <p className="text-xs text-emp-primary mt-1">{roleLabel}</p>}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setPwOpen(true)} className="cursor-pointer min-h-[44px]">
                      <Key className="mr-2 h-4 w-4" strokeWidth={1.5} /> Alterar Senha
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={async () => { await signOut(); navigateWithPreviewToken('/auth'); }}
                      className="cursor-pointer text-destructive focus:text-destructive min-h-[44px]"
                    >
                      <LogOut className="mr-2 h-4 w-4" strokeWidth={1.5} /> Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          )}
        </div>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
};

export default EmpHeader;
